import { ZfBox, ZfIcon, ZfIconButton, ZfLoadingSpinner, ZfPopover, ZfPopoverHeader, ZfTextButton, ZfTypography } from '@zafire/react';
import { type FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient } from '../../app/api';
import type { FamilyTreeNode, PartnerNodeData } from '../interfaces';
import { type PartnerDTO, PreselectedRelationshipType, type SourceRelation } from '../types/PartnerRelation';
import { CreatePartnerRelationModal } from './partner-data/CreatePartnerRelationModal';

interface IProps {
    partnerUuid: string;
    nodes: FamilyTreeNode[];
}

export const FamilyTreeHeader: FunctionComponent<IProps> = ({ partnerUuid, nodes }) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const { data: client, isLoading: isLoadingClient } = useClient(partnerUuid);

    const sourceRelations: SourceRelation[] = nodes
        .filter((node) => !node.data.isConnector) // only Partner nodes
        .map((node) => {
            const data = node.data as PartnerNodeData;

            return {
                partnerUuid: data.uuid,
                globalPartnerId: data.globalPartnerId,
                fullName: `${data.firstName} ${data.lastName}`.trim(),
            };
        });

    const availablePartners: PartnerDTO[] = sourceRelations.map((relation) => ({
        fullName: relation.fullName,
        globalPartnerId: relation.globalPartnerId!,
        techId: relation.partnerUuid,
    }));

    if (isLoadingClient) return <ZfLoadingSpinner size="extra-large" style={{ margin: 'auto' }} />;

    return (
        <div className="family-tree__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h4 style={{ margin: 0 }}>{t('familyTreeTitle')}</h4>
                <ZfPopover side={'right'}>
                    <ZfIconButton hierarchy="tertiary" size={'small'} slot={'anchor'} icon={'info'} />
                    <ZfPopoverHeader>{t('infoPopoverHeader')}</ZfPopoverHeader>
                    <ZfBox>
                        <ZfTypography>{t('infoPopoverContent')}</ZfTypography>
                    </ZfBox>
                </ZfPopover>
            </div>
            <div>
                {sourceRelations.length > 1 && (
                    <ZfTextButton size="medium" onClick={() => setIsModalOpen(true)} hierarchy="secondary">
                        <ZfIcon name="add_circle" />
                        {t('familyTreeNoRelationsNodeButton')}
                    </ZfTextButton>
                )}
                {isModalOpen && (
                    <CreatePartnerRelationModal
                        partnerUuid={partnerUuid}
                        sourceRelations={sourceRelations}
                        availablePartners={availablePartners}
                        relationshipManagerUserId={client?.relationshipManager?.userId}
                        onModalClose={() => setIsModalOpen(false)}
                        preselectedRelationshipType={PreselectedRelationshipType.IS_PARENT}
                    />
                )}
            </div>
        </div>
    );
};
